import { useEffect, useMemo, useRef } from "react";
import {
  Toast,
  ToastPortal,
  ToastProvider,
  ToastViewport,
  createToastManager,
  useToastManager,
} from "@/frontend/components/ui/toast";
import { useGlobalStore } from "../store/GlobalStore";
import { FusaToast, FusaToastType } from "./types";
import { StatusToast } from "./StatusToast";

// How long a toast stays up; 0 keeps it until it is closed. The same delays as
// Veriform Desktop's toasts.
function timeoutFor(toast: FusaToast) {
  if (!toast.autohide) {
    return 0;
  }
  return toast.type === FusaToastType.WARNING ? 8000 : 5000;
}

function ToastList() {
  const { toasts } = useToastManager<FusaToast>();

  return toasts.map((managed) => managed.data && (
    // Figma: 8px radius, #f8f8f8 card, 10% black border.
    <Toast key={managed.id} toast={managed} className="rounded-xl border-foreground/10 bg-card text-card-foreground">
      <StatusToast toast={managed.data} />
    </Toast>
  ));
}

/**
 * Shows the toasts in the global store with shadcn's Toast, laid out as
 * Veriform Desktop lays out its own. The store stays the source of truth for
 * which toasts exist; the toast manager only renders them. Each toast is handed
 * to the manager once, and leaves the store when the manager has finished
 * closing it, after its exit animation.
 */
export function FusawareToast() {
  const { toasts, removeToast } = useGlobalStore();
  const toastManager = useMemo(() => createToastManager(), []);
  // Toasts are keyed by message, as the store dedupes them.
  const handedToManager = useRef(new Set<string>());

  useEffect(() => {
    toasts.forEach((toast) => {
      if (handedToManager.current.has(toast.message)) {
        return;
      }
      handedToManager.current.add(toast.message);
      // Default (polite) priority even for errors: Base UI aria-hides
      // high-priority toasts and announces them separately, which would hide
      // their link and details from assistive tech.
      toastManager.add({
        id: toast.message,
        timeout: timeoutFor(toast),
        data: toast,
        onRemove: () => {
          handedToManager.current.delete(toast.message);
          removeToast(toast);
        },
      });
    });
  }, [toasts, toastManager, removeToast]);

  return (
    <ToastProvider toastManager={toastManager}>
      <ToastPortal>
        <ToastViewport className="print:hidden sm:max-w-[min(50vw,40rem)]">
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  );
}
